import { deepObjectFreeze } from 'utils/deep-object-freeze/deep-object-freeze';
import { routes } from 'utils/constants';
import { clearLocalStorageForSession } from 'utils/localStorage';
import { ParsedUrlQuery } from 'querystring';
import { IS_PRODUCTION } from 'utils/check-environment/check-environment';
import { DatadogClient, UrlManipulation } from '@workivate/tho-web-shared';

const urlFromClientName: { [key: string]: string | undefined } =
  deepObjectFreeze({
    'web-admin': import.meta.env.URL_ZEUS,
    web: import.meta.env.URL_HERMES,
    ios: import.meta.env.URL_IOS,
    android: import.meta.env.URL_ANDROID,
  } as const);

const localhostAddresses: { [key: string]: string | undefined } = {
  'web-admin': 'http://localhost:3001',
  web: 'https://app.localhost:3000',
};

/**
 * Maps a clientID to a pre-defined URL
 * | clientID : web-admin-06466845-e80b-4a80-9576-dbadf210ac26
 * @param clientId
 * @returns URL string to the redirect location
 */
export const getRedirectURLFromClientID = (
  clientId?: string,
  redirect_uri?: string
): string | undefined => {
  const clientName = getClientNameFromClientID(clientId);

  if (!IS_PRODUCTION && redirect_uri?.includes('localhost')) {
    return localhostAddresses[clientName];
  }

  return urlFromClientName[clientName];
};

/**
 * Maps a clientID to an app name
 * | clientID : web-admin-06466845-e80b-4a80-9576-dbadf210ac26
 * @param clientID
 * @returns App name string
 * Note that the client name may contain a hyphen so we cannot use it as a key
 * clientId is constructed as <client name>. <hyphen> <36 character UUID>
 */
export const getClientNameFromClientID = (clientId?: string): string => {
  const validClientId = clientId ?? '';

  const UUIDLength = 36; // UUID
  const charactersToTrim = UUIDLength + 1; // hyphen
  if (validClientId.length <= charactersToTrim) {
    return '';
  }
  return validClientId.substring(0, validClientId.length - charactersToTrim);
};

// type safe helper to get error status
export const getErrorStatus = (error: any) =>
  typeof error === 'object' && error !== null && 'status' in error
    ? error.status
    : undefined;

// type safe helper to get error type
export const getErrorType = (error: any) => {
  return typeof error === 'object' && error !== null && 'type' in error
    ? error.type
    : undefined;
};

type GetRedirectURIProps = {
  redirect_uri?: string | string[];
  shouldPersistLoginPageLanguage: boolean;
};

// the redirect_uri we send to the back end is the bare URL without any parameters
export const getRedirectURI = ({ redirect_uri }: GetRedirectURIProps) => {
  const redirectURIWithoutParams =
    typeof redirect_uri === 'string'
      ? redirect_uri.split('?')[0]
      : redirect_uri;

  return redirectURIWithoutParams;
};

export const getIsNative = (query: ParsedUrlQuery) => {
  return query?.is_native === 'true' || query?.is_native === '1';
};

type GetAppLoginURIProps = {
  redirect_uri?: string | string[];
  isLoginPersistent?: boolean;
};

export const redirectToAppAndLogIn = ({
  redirect_uri,
  isLoginPersistent = false,
}: GetAppLoginURIProps) => {
  if (redirect_uri && typeof redirect_uri === 'string') {
    let effectiveUrl = redirect_uri;
    // Append persistence param to the redirect URI.
    // This is the flow where the user came direct from USL FE
    // and was able to choose whether to persist their login
    // so we pass a value to the originating app which will override the app default
    // The originating app makes the decision in the flows where the user did not come direct from USL FE

    // we use the UrlManipulation helper to ensure we preserve the encoding of any existing params
    // and ensure cross-platform compatibility
    // otherwise we would have to perform string manipulation and would have to think about whether there may be any params or hashes already present
    effectiveUrl = UrlManipulation.modifyUrlParams<string>({
      url: redirect_uri,
      operations: [
        { name: 'persistence', value: isLoginPersistent ? 'true' : 'false' },
      ],
    });

    clearLocalStorageForSession();
    window.location.href = effectiveUrl;
  }
};

export type RedirectBackToClientHelperProps = {
  clientId?: string;
  error?: string;
  redirectUri?: string;
  searchParams?: URLSearchParams;
  navigate?: (path: string, options?: { replace?: boolean }) => void;
};

// a helper function is easy to test
// but it is only used in Session Context as redirectBackToClient
// which provides clientId
export const redirectBackToClientHelper = ({
  clientId,
  error,
  redirectUri,
  searchParams,
  navigate,
}: RedirectBackToClientHelperProps) => {
  let redirectUrl = getRedirectURLFromClientID(clientId, redirectUri);

  if (!redirectUrl) {
    if (searchParams && navigate) {
      redirectToErrorPage(searchParams, navigate);
    }
    return;
  }

  clearLocalStorageForSession();

  if (error) {
    redirectUrl = `${redirectUrl}?error=${error}`;
  }

  // Add correlation_id to the redirect URL for cross-app tracking
  // USL should never generate a new correlation ID - only pass through existing ones
  redirectUrl = DatadogClient.addCorrelationIdToUrl({
    url: redirectUrl,
    generateIfMissing: false,
  });

  window.location.href = redirectUrl;
};

export const redirectToErrorPage = (
  searchParams: URLSearchParams,
  navigate: (path: string, options?: { replace?: boolean }) => void
) => {
  const errorPath = buildUrlWithParams(routes['/error'], searchParams);
  navigate(errorPath, { replace: true });
};

/**
 * Builds a URL with query parameters using iOS-safe encoding.
 *
 * This function uses UrlManipulation from tho-web-shared to preserve %20 encoding
 * for spaces instead of + (form-urlencoded), which iOS apps do not decode correctly.
 * This prevents authentication failures when redirecting to external applications.
 *
 * @param url - The base URL path (e.g., '/login', '/forgotten-password')
 * @param searchParams - URLSearchParams to append to the URL
 * @returns URL with properly encoded query parameters, or the base URL if no params
 *
 * @example
 * const [searchParams] = useSearchParams();
 * const loginUrl = buildUrlWithParams('/login', searchParams);
 * // Result: '/login?redirect_uri=https%3A%2F%2Fapp.com%2Fpath%3Fuser%3DJohn%20Doe'
 * // Note: Spaces encoded as %20 (iOS-safe), not + (iOS-unsafe)
 */
export const buildUrlWithParams = (
  url: string,
  searchParams: URLSearchParams
): string => {
  const operations = Array.from(searchParams.entries()).map(
    ([name, value]) => ({ name, value })
  );

  return operations.length > 0
    ? UrlManipulation.modifyUrlParams({ url, operations })
    : url;
};
